import { AlertV2, Spinner, StandardTabsV2 } from '@cimplico/components';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFirmContext } from '../providers/FirmProvider';
import { useWorkpaperExport } from '../queries/use-workpaper-export';
import { WorkpaperExportEnum } from '../types/workpaper';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import ErrorSplashScreen from './ErrorSplashScreen';
import PageTitle from '../components/PageTitle';
import EditExportConfigForm from '../features/edit-workpaper/EditExportConfigForm';
import EditWorkpaperForm from '../features/edit-workpaper/EditWorkpaperForm';
import EditIntegrations from '../features/edit-workpaper/EditIntegrations';
import XeroErrorAlert from '../components/XeroErrorAlert';
import ErrorAlert from '../components/ErrorAlert';
import WorkpaperAccountsMappingContainer from '../features/workpaper-accounts-mapping/WorkpaperAccountsMappingContainer';
import { useTabParams } from '../hooks/use-tabs';
import { useWorkpaper } from '../queries/use-workpaper';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../intercom-events';

const tabs = ['details', 'export', 'integrations', 'chart'];

function WorkpaperSettingsPage(): JSX.Element {
    const { workpaperId } = useParams();
    const workpaperQuery = useWorkpaper(workpaperId);
    const { shortId } = useFirmContext();
    const { entity } = useWorkpaperContext();
    const [successfulMappings, setSuccessfulMappings] = useState<{ workpaperId: string; accountsConfirmed: number } | null>(null);
    const { trackEvent } = useIntercom();

    const { currentIndex, setTab } = useTabParams(tabs, 'details');

    const [xeroError, setXeroError] = useState<{ name: string; message: string } | undefined>();

    const workpaperExportQuery = useWorkpaperExport(workpaperId);

    useEffect(() => {
        if (successfulMappings) {
            trackEvent(
                intercomEvents.account.mapWorkpaperAccounts.event,
                intercomEvents.account.mapWorkpaperAccounts.metaData(
                    successfulMappings.workpaperId,
                    successfulMappings.accountsConfirmed,
                    'workpaper settings'
                )
            );
            setSuccessfulMappings(null);
        }
    }, [successfulMappings]);

    function handleTabChange(tabIndex: number): void {
        setTab(tabIndex);
    }

    if (workpaperExportQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (workpaperExportQuery.isError || !workpaperExportQuery.isSuccess) {
        if (workpaperExportQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Workpaper not found."
                    redirectTitle="Back to workpapers"
                    redirectUrl={`/${shortId}/users`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to workpapers"
                redirectUrl={`/${shortId}/workpapers`}
                leftArrow={true}
            />
        );
    }

    const workpaperExport = workpaperExportQuery.data.workpaperExport;
    const workpaper = workpaperQuery.data?.workpaper;

    return (
        <>
            {xeroError && (
                <div className="w-fit mx-auto mb-8">
                    <XeroErrorAlert
                        message={xeroError.message}
                        errorName={xeroError.name}
                        entityName={entity?.name}
                        entityId={entity?.id}
                    />
                </div>
            )}
            {workpaperExport.status === WorkpaperExportEnum.EXPORTING && (
                <AlertV2
                    title="Workpaper export is in progress..."
                    type="info"
                    description={'You will be notified once it is ready.'}
                    className="pb-4"
                />
            )}
            {workpaperExport.status === WorkpaperExportEnum.EXPORTED && (
                <AlertV2 title="Workpaper export is completed and ready for download." type="success" className="pb-4" />
            )}
            {workpaperExport.status === WorkpaperExportEnum.ERROR && workpaperExport.exportErrorReason && (
                <div className="pb-4">
                    <ErrorAlert message={workpaperExport.exportErrorReason.message} />
                </div>
            )}
            <div className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <PageTitle title="Settings" />
                </div>
                <div className="mx-auto mt-2 border-b">
                    <StandardTabsV2 onChange={setTab} name="workpaper-settings" selectedIndex={currentIndex}>
                        <StandardTabsV2.Tab label="Details" />
                        <StandardTabsV2.Tab label="Export Configuration" />
                        <StandardTabsV2.Tab label="Integrations" />
                        <StandardTabsV2.Tab label="Chart of Accounts" />
                    </StandardTabsV2>
                </div>
                <section className="mt-4">
                    {currentIndex === 0 && <EditWorkpaperForm onXeroError={setXeroError} />}
                    {currentIndex === 1 && <EditExportConfigForm workpaperExport={workpaperExport} />}
                    {currentIndex === 2 && <EditIntegrations />}
                    {currentIndex === 3 && workpaper && (
                        <WorkpaperAccountsMappingContainer workpaper={workpaper} entity={entity} onMap={setSuccessfulMappings} />
                    )}
                </section>
            </div>
        </>
    );
}

export default WorkpaperSettingsPage;
